import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { appendQueryString } from '../utils/queryParams';
import { extractFilenameFromDisposition, triggerBlobDownload } from '../utils/blobDownload';
import type {
  AuditLog,
  AuditLogDetailResponse,
  AuditLogFilters,
  AuditLogListResponse,
  AuditLogsResponse,
  CreateAuditLogPayload,
  LocalAuditLog,
} from '../types/auditLog';

const LS_KEY = 'saathi_audit_logs';
const MAX_LOCAL_ENTRIES = 2000;

const parseBlobErrorMessage = async (blob: Blob): Promise<string | null> => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error;
    }
  } catch {
    // Ignore parse failures
  }
  return null;
};

const buildAuditLogFilterUrl = (path: string, params?: AuditLogFilters): string =>
  appendQueryString(path, {
    page: params?.page,
    limit: params?.limit,
    search: params?.search,
    module: params?.module,
    subModule: params?.subModule,
    action: params?.action,
    status: params?.status,
    adminId: params?.adminId,
    startDate: params?.startDate,
    endDate: params?.endDate,
  });

const cleanFilterParams = (params: AuditLogFilters = {}): AuditLogFilters =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null)
  ) as AuditLogFilters;

const normalizeListResponse = (response: AuditLogListResponse): AuditLogsResponse => ({
  data: response.data.items,
  metaData: {
    page: response.data.page,
    limit: response.data.limit,
    total: response.data.total,
    totalPages: response.data.totalPages,
  },
  message: response.message,
});

function readLocalLogs(): LocalAuditLog[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as LocalAuditLog[]) : [];
  } catch {
    return [];
  }
}

function writeLocalLogs(logs: LocalAuditLog[]): void {
  try {
    const trimmed = logs.slice(-MAX_LOCAL_ENTRIES);
    localStorage.setItem(LS_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage might be full — silently ignore
  }
}

function appendLocalLog(log: LocalAuditLog): void {
  const logs = readLocalLogs();
  logs.push(log);
  writeLocalLogs(logs);
}

function generateLocalId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export const auditService = {
  /**
   * Send a single audit log entry to the backend.
   * Also stores it locally so the viewer works even before the backend endpoint exists.
   * Never throws — audit logging must never break the main app.
   */
  async create(payload: CreateAuditLogPayload): Promise<void> {
    const log: LocalAuditLog = {
      ...payload,
      id: generateLocalId(),
    };

    appendLocalLog(log);

    try {
      await apiClient.post(API_ENDPOINTS.auditLogs.create, payload);
    } catch {
      // Backend not yet wired — log is already in localStorage
    }
  },

  /**
   * Fetch paginated audit logs from the backend.
   */
  async list(params: AuditLogFilters = {}): Promise<AuditLogsResponse> {
    const cleanParams = cleanFilterParams(params);
    const response = await apiClient.get<AuditLogListResponse>(API_ENDPOINTS.auditLogs.list, {
      params: cleanParams,
    });
    return normalizeListResponse(response.data);
  },

  /**
   * Get a single log entry by id.
   */
  async getById(id: string): Promise<AuditLog> {
    const response = await apiClient.get<AuditLogDetailResponse>(API_ENDPOINTS.auditLogs.get(id));
    return response.data.data;
  },

  /**
   * Export audit logs as CSV using the same filters as the list API.
   */
  async exportReport(params?: AuditLogFilters): Promise<void> {
    const url = buildAuditLogFilterUrl(API_ENDPOINTS.auditLogs.export, cleanFilterParams(params));

    const response = await apiClient.get<Blob>(url, { responseType: 'blob' });
    const blob = response.data;
    const contentType = String(response.headers['content-type'] ?? '').toLowerCase();

    if (contentType.includes('application/json') || contentType.includes('text/plain')) {
      const message = (await parseBlobErrorMessage(blob)) ?? 'Failed to export audit logs.';
      throw new Error(message);
    }

    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename =
      extractFilenameFromDisposition(disposition)
      ?? `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;

    triggerBlobDownload(blob, filename);
  },

  /**
   * Clear all locally stored logs (used after successful backend sync, or by admin).
   */
  clearLocal(): void {
    localStorage.removeItem(LS_KEY);
  },
};
